import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import LegacyButton from '../LegacyButton';
import Icon from '../Icon';
import Typography from '../Typography';
import InputGroup from '../InputGroup';
import { useTheme } from '../../contextProviders';
import { useSelector } from 'react-redux';
import { differenceInDays, parseISO, isAfter, isBefore } from 'date-fns';
import { Link } from 'react-router-dom';
import { _roleType } from '@custom/Utils/constants/appConstants';

const StudyListFilter = ({
  filtersMeta,
  filterValues,
  onChange,
  clearFilters,
  isFiltering,
  numOfStudies,
  onUploadClick,
  getDataSourceConfigurationComponent,
  title,
  count,
  studiesToSend,
  handleDownloadExcel,
  duplicateStudyHandler,
  setMergeModal,
  handleAddToResearch,
  handleDownloadDicomClick,
  handleCreateISO,
  generateStudyLinkHandler,
  isSelectAll,
  handleSelectAllClick,
  handleMoveToExtraStorage,
  handleDeleteDicomClick
}) => {
  const { t } = useTranslation('StudyList');
  const state = useSelector(state => state);
  const role = sessionStorage.getItem('role') || localStorage.getItem('role');
  const { sortBy, sortDirection } = filterValues;
  const filterSorting = { sortBy, sortDirection };
  const setFilterSorting = sortingValues => {
    onChange({
      ...filterValues,
      ...sortingValues,
    });
  };
  const getSubscriptionStatus = (expiryDate) => {
    if (!expiryDate) return null;

    const today = new Date();
    const expiry = parseISO(expiryDate);
    const daysAfterExpiry = differenceInDays(today, expiry);
    const daysUntilExpiry = differenceInDays(expiry, today);


    const isExpired = isAfter(today, expiry);
    if (daysAfterExpiry >= 7) {
      return {
        status: 'terminated',
        message: 'Your plan has expired. Please renew or upgrade your plan to continue enjoying uninterrupted access.'
      };
    }
    if (isExpired) {
      const daysLeft = Math.max(0, Math.ceil(7 - daysAfterExpiry));
      const deletionDate = new Date(expiry);
      deletionDate.setDate(deletionDate.getDate() + 7); const formattedDate = deletionDate.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).replace(/,/g, '');
      return {
        status: 'expired',
        daysInRetentionPeriod: daysLeft,
        message: `Your plan has expired. You are now in a ${daysLeft} day${(daysLeft === 1 || daysLeft === 0) ? '' : 's'} grace period. Please upgrade your package to continue full access, or download your studies before the grace period ends.`
      };
    }

    if (daysUntilExpiry <= 7) {
      return {
        status: 'warning',
        daysRemaining: daysUntilExpiry,
        message: `Your current plan will expire in ${daysUntilExpiry} day${(daysUntilExpiry === 1 || daysUntilExpiry === 0) ? '' : 's'}. Please make sure to renew or upgrade it before the expiry to avoid any interruption in service.`
      };
    }

    return null;
  };

  const getExtraStorageSubscriptionStatus = (extraStorageArr) => {
    if (!Array.isArray(extraStorageArr) || extraStorageArr.length === 0) return null;

    const allDates = extraStorageArr
      .map(obj => Object.values(obj)[0])
      .filter(dateStr => !!dateStr);

    if (allDates.length === 0) return null;

    // Find the newest (latest) date
    const newestDateStr = allDates.reduce((latest, curr) => {
      return new Date(curr) > new Date(latest) ? curr : latest;
    }, allDates[0]);

    const today = new Date();
    const expiry = parseISO(newestDateStr);
    const daysAfterExpiry = differenceInDays(today, expiry);
    const daysUntilExpiry = differenceInDays(expiry, today);
    const isExpired = isAfter(today, expiry);

    if (daysAfterExpiry >= 7) {
      return {
        status: 'terminated',
        message: 'Your extra storage plan has expired. Please renew or upgrade your plan to continue uninterrupted access to extra storage.'
      };
    }
    if (isExpired) {
      const daysLeft = Math.max(0, Math.ceil(7 - daysAfterExpiry));
      const deletionDate = new Date(expiry);
      deletionDate.setDate(deletionDate.getDate() + 7);
      const formattedDate = deletionDate.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).replace(/,/g, '');
      return {
        status: 'expired',
        daysInRetentionPeriod: daysLeft,
        message: `Your extra storage plan has expired. You are now in a ${daysLeft} day${(daysLeft === 1 || daysLeft === 0) ? '' : 's'} grace period. Please upgrade your package to continue full access, or download your data before the grace period ends.`
      };
    }
    if (daysUntilExpiry <= 7) {
      return {
        status: 'warning',
        daysRemaining: daysUntilExpiry,
        message: `Your extra storage plan will expire in ${daysUntilExpiry} day${(daysUntilExpiry === 1 || daysUntilExpiry === 0) ? '' : 's'}. Please make sure to renew or upgrade it before the expiry to avoid any interruption in service.`
      };
    }
    return null;
  };

  const isSortingEnabled = numOfStudies > 0 && numOfStudies <= 100;
  const { isDarkMode } = useTheme();

  return (
    <React.Fragment>
      <div>
        <div className={isDarkMode ? "bg-white" : "bg-black"}>
          <div className="relative mx-auto flex flex-col">
            <div className="flex flex-row justify-end">
              <div className="flex min-w-[1px] shrink flex-row items-center gap-6">
                {/* <Typography
                  variant="h6"
                  className={isDarkMode ? "text-black" : "text-white"}
                >
                  {title ? t('PatientList'):t('StudyList')}
                </Typography> */}
                {getDataSourceConfigurationComponent && getDataSourceConfigurationComponent()}
                {onUploadClick && (
                  <div
                    className="text-primary-active flex cursor-pointer items-center gap-2 self-center text-lg font-semibold"
                    onClick={onUploadClick}
                  >
                    <Icon name="icon-upload"></Icon>
                    <span>{t('Upload')}</span>
                  </div>
                )}
              </div>
              <div className="flex h-[34px] flex-row items-center">
                {/* TODO revisit the completely rounded style of button used for clearing the study list filter - for now use LegacyButton*/}
                {isFiltering && (
                  <LegacyButton
                    rounded="full"
                    variant="outlined"
                    color="primaryActive"
                    border="primaryActive"
                    startIcon={<Icon name="cancel" />}
                    onClick={clearFilters}
                  >
                    {t('ClearFilters')}
                  </LegacyButton>
                )}
                {/* <Typography
                  variant="h6"
                  className={isDarkMode ? "text-black" : "text-primary-light"}
                >
                  {title ? `${t('Number of Patients')}: `:`${t('Number of studies')}: `}
                </Typography>
                <Typography
                  variant="h6"
                  className={isDarkMode ? "text-black mr-2" : "mr-2"}
                  data-cy={'num-studies'}
                >
                  {numOfStudies > 100 ? '>100' : count }
                </Typography> */}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={isDarkMode ? "sticky -top-1 z-10 border-b-4 border-white" : "sticky -top-1 z-10 border-b-4 border-black"}>
        <div className={isDarkMode ? "bg-primary-dark pt-3 pb-3" : "bg-white pt-3 pb-3"}>
          <div className="relative mx-auto flex flex-col">
            {((role === _roleType.hospitalSuperadmin || role === _roleType.admin) && state?.hospitalDetailsResponse?.result?.subscriptionDetail?.expiry_date) && (
              <>
                {(() => {
                  const subscriptionStatus = getSubscriptionStatus(
                    state.hospitalDetailsResponse.result.subscriptionDetail.expiry_date
                  );

                  if (!subscriptionStatus) return null; return (<div
                    style={{
                      marginBottom: '0.5rem',
                      padding: '0.75rem 1rem',
                      borderRadius: '0.375rem',
                      backgroundColor: subscriptionStatus.status === 'terminated'
                        ? '#f3f4f6'
                        : subscriptionStatus.status === 'expired'
                          ? '#fee2e2'
                          : '#fef3c7',
                      borderLeft: `4px solid ${subscriptionStatus.status === 'terminated'
                        ? '#6b7280'
                        : subscriptionStatus.status === 'expired'
                          ? '#ef4444'
                          : '#f59e0b'}`,
                      color: subscriptionStatus.status === 'terminated'
                        ? '#374151'
                        : subscriptionStatus.status === 'expired'
                          ? '#b91c1c'
                          : '#92400e'
                    }}
                    role="alert"
                  >
                    <div className="flex items-center">
                      <div className="py-1">
                        <svg
                          className={`mr-4 h-6 w-6 ${subscriptionStatus.status === 'terminated'
                            ? 'text-gray-500'
                            : subscriptionStatus.status === 'expired'
                              ? 'text-red-500'
                              : 'text-yellow-500'
                            }`}
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div>
                        <p style={{ fontWeight: 'bold' }}>{subscriptionStatus.message}</p>
                        {(subscriptionStatus.status === 'expired' || subscriptionStatus.status === 'warning') && (
                          <p className="text-sm mt-1">
                            <Link
                              to={'/edithospital'}
                              style={{ fontWeight: 600 }}
                              className="underline hover:text-red-800"
                            >
                              Upgrade your plan
                            </Link>
                            {' or contact support for assistance.'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })()}
              </>
            )}
          </div>
          <div className="relative mx-auto flex flex-col">
            {((role === _roleType.hospitalSuperadmin || role === _roleType.admin)) && state?.subscribedStudiesSliceResponse?.result?.extraStoragePlan?.extraStorageExpiryDate && (
              <>
                {(() => {
                  const subscriptionStatus = getExtraStorageSubscriptionStatus(
                    state?.subscribedStudiesSliceResponse?.result?.extraStoragePlan?.extraStorageExpiryDate);

                  if (!subscriptionStatus) return null; return (<div
                    style={{
                      marginBottom: '0.5rem',
                      padding: '0.75rem 1rem',
                      borderRadius: '0.375rem',
                      backgroundColor: subscriptionStatus.status === 'terminated'
                        ? '#f3f4f6'
                        : subscriptionStatus.status === 'expired'
                          ? '#fee2e2'
                          : '#fef3c7',
                      borderLeft: `4px solid ${subscriptionStatus.status === 'terminated'
                        ? '#6b7280'
                        : subscriptionStatus.status === 'expired'
                          ? '#ef4444'
                          : '#f59e0b'}`,
                      color: subscriptionStatus.status === 'terminated'
                        ? '#374151'
                        : subscriptionStatus.status === 'expired'
                          ? '#b91c1c'
                          : '#92400e'
                    }}
                    role="alert"
                  >
                    <div className="flex items-center">
                      <div className="py-1">
                        <svg
                          className={`mr-4 h-6 w-6 ${subscriptionStatus.status === 'terminated'
                            ? 'text-gray-500'
                            : subscriptionStatus.status === 'expired'
                              ? 'text-red-500'
                              : 'text-yellow-500'
                            }`}
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div>
                        <p style={{ fontWeight: 'bold' }}>{subscriptionStatus.message}</p>
                        {(subscriptionStatus.status === 'expired' || subscriptionStatus.status === 'warning') && (
                          <p className="text-sm mt-1">
                            <Link
                              to={'/edithospital'}
                              style={{ fontWeight: 600 }}
                              className="underline hover:text-red-800"
                            >
                              Upgrade your plan
                            </Link>
                            {' or contact support for assistance.'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })()}
              </>
            )}
          </div>
          <InputGroup
            inputMeta={filtersMeta}
            values={filterValues}
            onValuesChange={onChange}
            sorting={filterSorting}
            onSortingChange={setFilterSorting}
            isSortingEnabled={isSortingEnabled}
            studiesToSend={studiesToSend}
            handleDownloadExcel={handleDownloadExcel}
            duplicateStudyHandler={duplicateStudyHandler}
            setMergeModal={setMergeModal}
            handleAddToResearch={handleAddToResearch}
            handleDownloadDicomClick={handleDownloadDicomClick}
            handleCreateISO={handleCreateISO}
            generateStudyLinkHandler={generateStudyLinkHandler}
            isSelectAll={isSelectAll}
            handleSelectAllClick={handleSelectAllClick}
            handleMoveToExtraStorage={handleMoveToExtraStorage}
            handleDeleteDicomClick={handleDeleteDicomClick}
          />
          {numOfStudies > 100 && (
            <div className="container m-auto">
              <div className="bg-primary-main rounded-b py-1 text-center text-base">
                <p className="text-white">{t('Filter list to 100 studies or less to enable sorting')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

StudyListFilter.propTypes = {
  filtersMeta: PropTypes.arrayOf(
    PropTypes.shape({
      /** Identifier used to map a field to it's value in `filterValues` */
      name: PropTypes.string.isRequired,
      /** Friendly label for filter field */
      displayName: PropTypes.string.isRequired,
      /** One of the supported filter field input types */
      inputType: PropTypes.oneOf(['Text', 'MultiSelect', 'DateRange', 'None']).isRequired,
      isSortable: PropTypes.bool.isRequired,
      /** Size of filter field in a 12-grid system */
      gridCol: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).isRequired,
      /** Options for a "MultiSelect" inputType */
      option: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.string,
          label: PropTypes.string,
        })
      ),
    })
  ).isRequired,
  filterValues: PropTypes.object.isRequired,
  numOfStudies: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  // isFiltering: PropTypes.bool.isRequired,
  onUploadClick: PropTypes.func,
  title: PropTypes.string,
  getDataSourceConfigurationComponent: PropTypes.func,
};

export default StudyListFilter;
